import { Popup } from 'react-map-gl/maplibre';
import { Separator } from 'shared-ui';

import { StatusPill } from '@/components/shared/StatusPill';
import { getVanStatePillPresentation } from '@/components/shared/statusPillPresentation';
import { vanPanelTitle } from '../utils/vanFeatureProperties';
import type { VanFeatureProperties } from '../utils/vanFeatureProperties';

interface VanPopupProps {
    van: VanFeatureProperties;
    /** Geographic anchor — MapLibre projects + tracks this itself, so no screen-pixel math here. */
    longitude: number;
    latitude: number;
}

// Label left, value right — the design's two-column fact rows. Labels are Poppins Medium
// text-sm/leading-5, values Regular text-sm/leading-5 in muted-foreground, both
// tracking-normal (Poppins is the app-wide --font-sans, so no font utility needed). An em
// dash keeps a missing fact visible rather than collapsing the row.
function FactRow({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <>
            <dt className="text-foreground text-sm leading-5 font-medium tracking-normal">{label}</dt>
            <dd className="text-muted-foreground text-sm leading-5 font-normal tracking-normal">{value ?? '—'}</dd>
        </>
    );
}

/**
 * Technician-van hover card, positioned by MapLibre's own `<Popup>` (the FittingPopup
 * pattern). `--passive` keeps this hover-only card from ever capturing the cursor.
 *
 * Per the design: the make/model as the uppercase title (the plate the provider folds
 * into the display name is stripped — it has its own row), the live state as the van
 * pill, then Operative and Registration as label/value rows.
 */
export function VanPopup({ van, longitude, latitude }: VanPopupProps) {
    const title = vanPanelTitle(van);
    return (
        <Popup
            longitude={longitude}
            latitude={latitude}
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={24}
            maxWidth="none"
            className="orbit-hover-popup orbit-hover-popup--passive"
        >
            <div className="border-border/30 bg-card pointer-events-none w-max min-w-[12rem] rounded-md border p-4 shadow-lg">
                {/* Poppins Black text-sm/leading-5, tracking-normal (Poppins is the app-wide
                    --font-sans, so no font utility needed). */}
                {title && (
                    <p className="text-foreground text-sm leading-5 font-black tracking-normal uppercase">{title}</p>
                )}
                <div className={title ? 'mt-2' : undefined}>
                    <StatusPill presentation={getVanStatePillPresentation(van.state)} />
                </div>
                {/* The POI hover card's divider between the status and its fields. */}
                <Separator className="my-2" />
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                    <FactRow label="Operative" value={van.operative_name} />
                    <FactRow label="Registration" value={van.registration} />
                </dl>
            </div>
        </Popup>
    );
}
